import { useEffect, useRef } from 'react'
import { useSceneStore } from '../../a.scenes/GameScene/store'
import { BATTLE_STATES, COMMANDERS, Spell } from '../../c.managers/LevelManager/store.d'
import { EVENTS_TICK, dispatch } from '../../b.providers/EventsProvider/EventsProvider'
import { CHARACTER_STATES, CHARACTER_TYPES } from '../../f.assets/Characters/scripts/constants'
import {
  ActionAwait,
  ActionInvalid,
  ActionLoot,
  ActionRespawn,
} from '../../f.assets/Characters/scripts/classes/CharacterActions'
import { useScenesStore } from '../../b.providers/ScenesProvider/store'
import getGameElementByUUID from '../../e.elements/GameElements/helpers/getGameElementByUUID'
import Minion from '../../e.elements/GameElements/classes/Minion'
import { TreasureConfig } from '../../f.assets/Characters/scripts/classes/TreasureConfig'
import getGameElementFromObj from '../../e.elements/GameElements/helpers/getGameElementFromObj'
import Treasure from '../../e.elements/GameElements/classes/Treasure'
import { TerrainSlice } from '../../c.managers/TerrainManager/store.d'
import Interactive from '../../e.elements/GameElements/classes/Interactive'
import { Characters } from '../CharactersController'
import { GAME_OBJECTS } from '../../e.elements/GameElements/classes/types'
import { SEL_GET_MINIONS } from '../../c.managers/CharactersManager/selectors'
import { Object3D } from 'three'
import { Audio } from '../AudioController'
import { SEL_TREASURES } from '../../c.managers/TerrainManager/selectors'
import { SEL_MANA_LEVEL_FOR_PLAYER } from '../../c.managers/LevelManager/selectors'

export const API = {
  getTresure: (owner: COMMANDERS) => {
    const treasureObj = useScenesStore.getState().getGameObject3D(`treasure_${owner}`)
    if (treasureObj) {
      return getGameElementFromObj(treasureObj) as Treasure
    }
  },
  getEnemyTresure: (owner: COMMANDERS) => {
    const enemy = useSceneStore.getState().levels.data.troopOwners.find(o => o !== owner)
    const treasureObj = useScenesStore.getState().getGameObject3D(`treasure_${enemy}`)
    if (treasureObj) {
      return getGameElementFromObj(treasureObj) as Treasure
    }
  },
  // this could be replaced by an event
  lootingEnemy: (enemy: COMMANDERS, amount: number) => {
    const treasureObj = useScenesStore.getState().getGameObject3D(`treasure_${enemy}`)
    if (treasureObj) {
      const treasure = getGameElementFromObj(treasureObj) as Treasure
      treasure.getsLooted(amount)
    }
  },
  updateTresure: (owner: COMMANDERS, treasure: Partial<TreasureConfig>) => {
    useSceneStore.getState().terrain.updateTreasure(owner, treasure)
  },
  getTresureState: (owner: COMMANDERS) => {
    const tresures = useSceneStore.getState().terrain.data.treasures.find(t => t.owner === owner)
    if (tresures) {
      return tresures
    }
  },
  resolveInteraction: (targetGameObject: Interactive) => {
    const selectionId = useSceneStore.getState().characters.data.selectedCharacterUUID
    const clearSelection = useSceneStore.getState().characters.clearSelectedCharacterUUID
    if (!selectionId) return console.log('no selection as interaction source')

    const sourceGameObject = getGameElementByUUID(selectionId) as Interactive

    // ----------------
    if (targetGameObject.isClass(GAME_OBJECTS.TREASURE)) {
      // console.log('is loot')
      Characters.addLootAction(sourceGameObject.uuid, targetGameObject.uuid)
      // unselect
    } else if (targetGameObject.isClass(GAME_OBJECTS.MINION) && !(targetGameObject as Minion).isInvalidated) {
      // console.log('is attack')
      Characters.addAttackToAction(sourceGameObject.uuid, targetGameObject.uuid)
    } else if (targetGameObject.isClass(GAME_OBJECTS.TILE)) {
      // console.log('is move to', targetGameObject)
      Characters.addGoToAction(sourceGameObject.uuid, targetGameObject.coords)
    }
    // clean up
    clearSelection()
    // sourceGameObject.isInteractionSource = false
    // targetGameObject.isInteractionTarget = false
  },
  updateManaUnits: (toWho: COMMANDERS, units: number) => {
    const { maxManaLevel } = SEL_MANA_LEVEL_FOR_PLAYER(useSceneStore.getState())
    const unitAmount = maxManaLevel / (maxManaLevel / 10)
    API.updateMana(toWho, unitAmount * units)
    // useSceneStore.getState().levels.updateMana(toWho, amount)
  },
  updateMana: (toWho: COMMANDERS, amount: number) => {
    useSceneStore.getState().levels.updateMana(toWho, amount)
  },
  recoverUnitHP: (unit: Minion, perc: number) => {
    const amount = unit.hp_max * perc
    Characters.updateState(unit.uuid, {
      hp: Math.min(unit.hp + amount, unit.hp_max),
    })
  },
  calcWinner: () => {
    let winner: COMMANDERS | undefined = undefined
    const treasure1 = useSceneStore.getState().terrain.data.treasures[0]
    const treasure2 = useSceneStore.getState().terrain.data.treasures[1]
    const minionStates = useSceneStore.getState().characters.data.characterStates
    if (treasure1.amount! > treasure2.amount!) {
      // console.log(treasure1.owner, 'WIN')
      winner = treasure1.owner
    } else if (treasure1.amount! < treasure2.amount!) {
      // trigger event for winning
      // console.log(treasure2.owner, 'WIN')
      winner = treasure2.owner
    } // else it's a draw

    if (winner) {
      Object.keys(minionStates).forEach(k => {
        const updateState = useSceneStore.getState().characters.updateCharacterState
        const minionState = minionStates[k]
        if (![CHARACTER_STATES.BURIED, CHARACTER_STATES.DEAD, CHARACTER_STATES.INACTIVE].includes(minionState.state)) {
          updateState(k, {
            state: minionState.owner === winner ? CHARACTER_STATES.CHEERING : CHARACTER_STATES.IDLE,
          })
        }
        const character = getGameElementByUUID(k) as Minion
        if (minionState.owner === winner) {
          character.teamWon()
          // Animations.celebrate(getGameElementByUUID(k).obj)
        } else {
          character.teamLost()
          // Animations.idle(getGameElementByUUID(k).obj)
        }
      })
    } else {
      // it's a draw
      Object.keys(minionStates).forEach(k => {
        const updateState = useSceneStore.getState().characters.updateCharacterState
        const minionState = minionStates[k]
        if (![CHARACTER_STATES.BURIED, CHARACTER_STATES.DEAD, CHARACTER_STATES.INACTIVE].includes(minionState.state)) {
          updateState(k, {
            state: CHARACTER_STATES.IDLE,
          })
        }
        const character = getGameElementByUUID(k) as Minion
        character.teamLost()
      })
    }
    return winner
  },
  finishBattle: () => {
    const winner = API.calcWinner()
    useSceneStore.getState().levels.setBattleState(BATTLE_STATES.ENDED)
    const homeTeam = useScenesStore.getState().user.team!
    const awayTeam = homeTeam === CHARACTER_TYPES.JACK ? CHARACTER_TYPES.WITCH : CHARACTER_TYPES.JACK
    Audio.finishBattle(winner === COMMANDERS.PLAYER ? homeTeam : awayTeam)
  },
}

export const TICK_TIME_MS = 1000
export const TICK_DIVISIONS = 4

const BattleController = () => {
  const tId = useRef<NodeJS.Timeout>()
  const tickRef = useRef<number>(0)
  const minionStates = useSceneStore(SEL_GET_MINIONS)
  const lootStates = useSceneStore(s => s.terrain.data.treasures)
  const batttleState = useSceneStore(s => s.levels.data.battleState)
  const updateCharacterState = useSceneStore(s => s.characters.updateCharacterState)
  const treasures = useSceneStore(SEL_TREASURES)
  // console.log(treasures[0]?.amount, treasures[1]?.amount);

  // const updateCharacterState = useSceneStore(s => s.)

  const tick = () => {
    const tickReminder = tickRef.current % TICK_DIVISIONS
    if (tickReminder === 0) {
      dispatch(EVENTS_TICK.TICK, tickReminder)
    }
    if ([0, 2].includes(tickReminder)) {
      dispatch(EVENTS_TICK.TICK_HALF, tickReminder)
    }
    if ([0, 1, 2, 3].includes(tickReminder)) {
      dispatch(EVENTS_TICK.TICK_QUARTER, tickReminder)
    }
    tickRef.current = tickReminder === 3 ? 0 : tickReminder + 1
  }

  useEffect(() => {
    tId.current = setInterval(tick, TICK_TIME_MS / TICK_DIVISIONS)
    return () => {
      clearInterval(tId.current)
    }
  }, [])

  useEffect(() => {
    if (Object.keys(minionStates).length > 0 && batttleState === BATTLE_STATES.ONGOING) {
      Object.keys(minionStates).forEach(k => {
        const minionState = minionStates[k]

        // behavior logic
        if (minionState.state === CHARACTER_STATES.IDLE) {
          // add action
          const booty = lootStates.find(l => l.owner !== minionState.owner)
          if (booty) {
            updateCharacterState(k, {
              state: CHARACTER_STATES.ACTING,
            })

            const minion = getGameElementByUUID(k) as Minion
            minion.loot()
            // minion.appendActions([new ActionLoot()])
          }
        }

        if (minionState.state === CHARACTER_STATES.INACTIVE) {
          updateCharacterState(k, {
            state: CHARACTER_STATES.BURIED,
          })
          const minion = getGameElementByUUID(k) as Minion
          const ActionsForDelay = Array(Math.floor(Math.random() * 8))
            .fill(0)
            .map(_ => new ActionInvalid())
          minion.appendActions([...ActionsForDelay, new ActionRespawn()])
        }
      })
    }
  }, [minionStates, batttleState])

  useEffect(() => {
    const treasure1Empty = treasures[0] && treasures[0].amount === 0
    const treasure2Empty = treasures[1] && treasures[1].amount === 0
    if (treasure1Empty || treasure2Empty) {
      clearInterval(tId.current)
      API.finishBattle()
    }
  }, [treasures[0], treasures[1]])

  return null
}

export default BattleController
